import type {
  AgentBotBindingAccess,
  AgentConfig,
  AgentMemory,
  AgentMemorySettings,
  AgentEvolutionOverview,
  AgentRegressionRun,
  AgentSummary,
  CustomSkill,
  Connector,
  BrowserBridgeDevice,
  BrowserBridgePairing,
  CodexUsageDevice,
  CodexUsageLeaderboard,
  CodexUsagePairing,
  BuilderApproval,
  BuilderAuditLog,
  BuilderConnection,
  BuilderConnectionCreated,
  BuilderScope,
  ConnectorSnapshot,
  CompanyModel,
  KnowledgeSource,
  MemoryProject,
  MemoryProjectDetail,
  GlobalMemorySettings,
  EmployeeProfile,
  ProductOptimizationOverview,
  RegressionAssertions,
  RegressionStatus
} from "./types";
import {
  FEISHU_CONNECTOR_AUTHORIZED_AT_KEY,
  FEISHU_OFFICE_AUTHORIZED_AT_KEY,
  FEISHU_OFFICE_WRITE_AUTHORIZED_AT_KEY,
  FEISHU_WRITE_AUTHORIZED_AT_KEY,
  KNOWLEDGE_AUTHORIZED_AT_KEY,
  hasRecentFeishuConnectorAuthorization,
  hasRecentFeishuOfficeAuthorization,
  hasRecentFeishuOfficeWriteAuthorization,
  hasRecentFeishuWriteAuthorization,
  hasRecentKnowledgeAuthorization
} from "./agent-activation";
import { isResumablePostAuthPath } from "./auth-redirect";

const DEV_USER_ID = localStorage.getItem("devUserId") || "dev-user-001";
const POST_AUTH_REDIRECT_KEY = "agent-platform-post-auth-redirect";

type FeishuAuthResult = { code: string };
type FeishuAuthError = { errno?: number; errString?: string };
type FeishuClient = {
  requestAccess?: (options: {
    appID: string;
    scopeList: string[];
    success: (result: FeishuAuthResult) => void;
    fail: (error: FeishuAuthError) => void;
  }) => void;
  requestAuthCode?: (options: {
    appId: string;
    success: (result: FeishuAuthResult) => void;
    fail: (error: FeishuAuthError) => void;
  }) => void;
};

declare global {
  interface Window {
    tt?: FeishuClient;
    h5sdk?: { ready: (callback: () => void) => void };
  }
}

const FEISHU_APP_ID = import.meta.env.VITE_FEISHU_APP_ID;
const FEISHU_SDK_URL = "https://lf-scm-cn.feishucdn.com/lark/op/h5-js-sdk-1.5.44.js";
const FEISHU_SDK_SCRIPT_ID = "feishu-h5-sdk";
export type AuthResult = { user: { id: string; name: string; department: string; isAdmin?: boolean } };
let authPromise:
  | Promise<AuthResult>
  | undefined;
let feishuSdkPromise: Promise<void> | undefined;

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const isFormData = init.body instanceof FormData;
  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(!isFormData ? { "content-type": "application/json" } : {}),
      "x-dev-user-id": DEV_USER_ID,
      ...init.headers
    }
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.message || `请求失败：${response.status}`) as Error & {
      status?: number;
      details?: unknown;
    };
    error.status = response.status;
    error.details = payload;
    throw error;
  }
  return payload as T;
}

function loadFeishuSdk(timeoutMs = 8_000): Promise<void> {
  if (window.h5sdk?.ready) return Promise.resolve();
  if (feishuSdkPromise) return feishuSdkPromise;

  feishuSdkPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(FEISHU_SDK_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing || document.createElement("script");
    const timeout = window.setTimeout(() => {
      feishuSdkPromise = undefined;
      reject(new Error("飞书客户端能力加载超时，请检查网络后重试"));
    }, timeoutMs);
    const finish = () => {
      window.clearTimeout(timeout);
      if (window.h5sdk?.ready) resolve();
      else {
        feishuSdkPromise = undefined;
        reject(new Error("飞书客户端能力未加载，请从飞书工作台重新打开应用"));
      }
    };
    script.addEventListener("load", finish, { once: true });
    script.addEventListener("error", () => {
      window.clearTimeout(timeout);
      feishuSdkPromise = undefined;
      reject(new Error("飞书客户端能力加载失败，请检查网络后重试"));
    }, { once: true });
    if (!existing) {
      script.id = FEISHU_SDK_SCRIPT_ID;
      script.src = FEISHU_SDK_URL;
      script.async = true;
      document.head.appendChild(script);
    }
  });
  return feishuSdkPromise;
}

async function waitForFeishuClient(timeoutMs = 8_000): Promise<FeishuClient> {
  await loadFeishuSdk(timeoutMs);

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(
      () => reject(new Error("飞书客户端初始化超时，请关闭页面后重新进入")),
      timeoutMs
    );

    window.h5sdk!.ready(() => {
      window.clearTimeout(timeout);
      if (window.tt?.requestAccess || window.tt?.requestAuthCode) {
        resolve(window.tt);
      } else {
        reject(new Error("当前飞书客户端不支持应用免登，请升级飞书后重试"));
      }
    });
  });
}

function requestLegacyAuthCode(tt: FeishuClient): Promise<string> {
  if (!tt.requestAuthCode) {
    throw new Error("当前飞书客户端不支持应用免登，请升级飞书后重试");
  }
  return new Promise((resolve, reject) => {
    tt.requestAuthCode!({
      appId: FEISHU_APP_ID,
      success: ({ code }) => resolve(code),
      fail: (error) => reject(new Error(error.errString || "飞书免登授权失败"))
    });
  });
}

interface FeishuAccessOptions {
  knowledge?: boolean;
  connector?: boolean;
  connectorWrite?: boolean;
  office?: boolean;
  officeWrite?: boolean;
}

function requestedFeishuScopes(options: FeishuAccessOptions = {}) {
  if (!options.knowledge && !options.connector && !options.connectorWrite && !options.office && !options.officeWrite) {
    return ["offline_access", "docx:document:readonly", "wiki:wiki:readonly"];
  }
  return [
    "offline_access",
    ...(options.knowledge
      ? [
          "docx:document:readonly",
          "wiki:wiki:readonly",
          "drive:drive:readonly",
          "sheets:spreadsheet:readonly"
        ]
      : []),
    ...(options.connector
      ? ["calendar:calendar:readonly", "calendar:calendar.event:read"]
      : []),
    ...(options.connectorWrite
      ? [
          "calendar:calendar.event:create",
          "calendar:calendar.event:update",
          "calendar:calendar.event:delete"
        ]
      : []),
    ...(options.office
      ? [
          "base:table:read",
          "base:field:read",
          "base:record:retrieve",
          "sheets:spreadsheet:readonly",
          "docx:document:readonly",
          "drive:drive:readonly",
          "search:docs:read",
          "wiki:wiki:readonly",
          "im:chat:read",
          "im:message:readonly",
          "contact:user:search",
          "contact:user.id:readonly",
          "calendar:calendar:readonly",
          "calendar:calendar.event:read"
        ]
      : []),
    ...(options.officeWrite
      ? [
          "base:record:create",
          "base:record:update",
          "sheets:spreadsheet:write_only",
          "docx:document:create",
          "docx:document:write_only",
          "space:folder:create",
          "wiki:node:create",
          "im:message",
          "im:message.send_as_user",
          "calendar:calendar.event:create",
          "calendar:calendar.event:update",
          "calendar:calendar.event:delete"
        ]
      : [])
  ];
}

async function requestFeishuAuthCode(options: FeishuAccessOptions = {}): Promise<string> {
  if (!FEISHU_APP_ID) throw new Error("应用未配置飞书 App ID");
  const tt = await waitForFeishuClient();

  if (!tt.requestAccess) return requestLegacyAuthCode(tt);

  try {
    return await new Promise<string>((resolve, reject) => {
      tt.requestAccess!({
        appID: FEISHU_APP_ID,
        scopeList: requestedFeishuScopes(options),
        success: ({ code }) => resolve(code),
        fail: reject
      });
    });
  } catch (error) {
    const feishuError = error as FeishuAuthError;
    if (feishuError.errno === 103 && tt.requestAuthCode) {
      return requestLegacyAuthCode(tt);
    }
    throw new Error(feishuError.errString || "飞书免登授权失败");
  }
}

async function authenticate() {
  try {
    return await request<AuthResult>("/auth/me");
  } catch (error) {
    if ((error as Error & { status?: number }).status !== 401) throw error;
    if (window.location.pathname !== "/agents") {
      const resumePath = `${window.location.pathname}${window.location.search}`;
      if (isResumablePostAuthPath(resumePath)) {
        sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, resumePath);
      }
      window.location.replace("/agents");
      return await new Promise<never>(() => undefined);
    }
    const code = await requestFeishuAuthCode();
    return request<AuthResult>("/auth/feishu/exchange", {
      method: "POST",
      body: JSON.stringify({ code })
    });
  }
}

export function ensureAuth() {
  authPromise ??= authenticate().catch((error) => {
    authPromise = undefined;
    throw error;
  });
  return authPromise;
}

export function consumePostAuthRedirect() {
  const value = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
  sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  return isResumablePostAuthPath(value) ? value : undefined;
}

async function authorizeFeishuAccess(options: FeishuAccessOptions) {
  const needsKnowledge = Boolean(options.knowledge)
    && !hasRecentKnowledgeAuthorization(sessionStorage);
  const needsConnector = Boolean(options.connector)
    && !hasRecentFeishuConnectorAuthorization(sessionStorage);
  const needsConnectorWrite = Boolean(options.connectorWrite)
    && !hasRecentFeishuWriteAuthorization(sessionStorage);
  const needsOffice = Boolean(options.office)
    && !hasRecentFeishuOfficeAuthorization(sessionStorage);
  const needsOfficeWrite = Boolean(options.officeWrite)
    && !hasRecentFeishuOfficeWriteAuthorization(sessionStorage);
  if (!needsKnowledge && !needsConnector && !needsConnectorWrite && !needsOffice && !needsOfficeWrite) return ensureAuth();
  const code = await requestFeishuAuthCode({
    knowledge: needsKnowledge,
    connector: needsConnector,
    connectorWrite: needsConnectorWrite,
    office: needsOffice,
    officeWrite: needsOfficeWrite
  });
  const result = await request<{ user: { id: string; name: string; department: string } }>(
    "/auth/feishu/exchange",
    { method: "POST", body: JSON.stringify({ code }) }
  );
  authPromise = Promise.resolve(result);
  const authorizedAt = String(Date.now());
  if (needsKnowledge) sessionStorage.setItem(KNOWLEDGE_AUTHORIZED_AT_KEY, authorizedAt);
  if (needsConnector) sessionStorage.setItem(FEISHU_CONNECTOR_AUTHORIZED_AT_KEY, authorizedAt);
  if (needsConnectorWrite) sessionStorage.setItem(FEISHU_WRITE_AUTHORIZED_AT_KEY, authorizedAt);
  if (needsOffice) sessionStorage.setItem(FEISHU_OFFICE_AUTHORIZED_AT_KEY, authorizedAt);
  if (needsOfficeWrite) sessionStorage.setItem(FEISHU_OFFICE_WRITE_AUTHORIZED_AT_KEY, authorizedAt);
  return result;
}

async function reauthorizeFeishuOfficeAccess() {
  // The employee is explicitly connecting the office connector. Always request
  // a fresh grant here: the server-side token may have expired or the app may
  // have added scopes after the browser's recent-authorization marker was set.
  const code = await requestFeishuAuthCode({ office: true, officeWrite: true });
  const result = await request<{ user: { id: string; name: string; department: string } }>(
    "/auth/feishu/exchange",
    { method: "POST", body: JSON.stringify({ code }) }
  );
  authPromise = Promise.resolve(result);
  const authorizedAt = String(Date.now());
  sessionStorage.setItem(FEISHU_OFFICE_AUTHORIZED_AT_KEY, authorizedAt);
  sessionStorage.setItem(FEISHU_OFFICE_WRITE_AUTHORIZED_AT_KEY, authorizedAt);
  return result;
}

async function refreshFeishuIdentity() {
  const tt = await waitForFeishuClient();
  const code = await requestLegacyAuthCode(tt);
  const result = await request<AuthResult>("/auth/feishu/exchange", {
    method: "POST",
    body: JSON.stringify({ code })
  });
  authPromise = Promise.resolve(result);
  return result;
}

export const api = {
  productOptimization: () => request<ProductOptimizationOverview>("/product-optimization"),
  updateProductOptimization: (
    id: string,
    body: { status?: "pending" | "approved" | "deferred" | "dismissed"; priority?: "p0" | "p1" | "p2" | "p3" }
  ) => request(`/product-optimization/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  runProductOptimization: () => request<{ queued: boolean; runDate: string; message: string }>(
    "/product-optimization/run", { method: "POST" }
  ),
  toolApprovals: () => request<import("./types").ToolApproval[]>("/tool-approvals"),
  decideToolApproval: (id: string, decision: "approve" | "reject") =>
    request<{ status: string; notificationSent?: boolean; errorMessage?: string }>(`/tool-approvals/${id}/${decision}`, { method: "POST" }),
  builderConnections: () => request<BuilderConnection[]>("/builder/connections"),
  createBuilderConnection: (body: {
    clientType: BuilderConnection["clientType"];
    clientName: string;
    scopes: BuilderScope[];
    expiresInDays: number;
  }) => request<BuilderConnectionCreated>("/builder/connections", {
    method: "POST",
    body: JSON.stringify(body)
  }),
  revokeBuilderConnection: (id: string) =>
    request<{ ok: boolean }>(`/builder/connections/${id}`, { method: "DELETE" }),
  builderApprovals: () => request<BuilderApproval[]>("/builder/approvals"),
  builderAuditLogs: () => request<BuilderAuditLog[]>("/builder/audit-logs"),
  decideBuilderApproval: (id: string, decision: "approve" | "reject") =>
    request(`/builder/approvals/${id}/${decision}`, { method: "POST" }),
  companyModels: () => request<CompanyModel[]>("/company-models"),
  templates: () =>
    request<Array<{ id: string; name: string; description: string; icon: string }>>(
      "/agent-templates"
    ),
  mine: (q = "") => request<AgentSummary[]>(`/agents/mine?q=${encodeURIComponent(q)}`),
  market: (q = "") => request<AgentSummary[]>(`/agents/market?q=${encodeURIComponent(q)}`),
  getAgent: (id: string) => request<AgentSummary>(`/agents/${id}`),
  agentEvolution: (id: string, days: 7 | 30 | 90 = 30) =>
    request<AgentEvolutionOverview>(`/agents/${id}/evolution?days=${days}`),
  updateEvolutionCandidate: (
    agentId: string,
    candidateId: string,
    input: {
      status?: "candidate" | "accepted" | "dismissed";
      scenarioTitle?: string;
      testInput?: string | null;
      expectedBehavior?: string;
      assertions?: RegressionAssertions;
    }
  ) => request<{ id: string; status: string; updatedAt: string }>(
    `/agents/${agentId}/evolution/eval-candidates/${candidateId}`,
    { method: "PATCH", body: JSON.stringify(input) }
  ),
  createEvolutionCase: (agentId: string, input: {
    scenarioTitle: string;
    testInput: string;
    expectedBehavior: string;
    assertions: RegressionAssertions;
  }) => request(`/agents/${agentId}/evolution/eval-candidates`, {
    method: "POST",
    body: JSON.stringify(input)
  }),
  runAgentRegression: (agentId: string) =>
    request<AgentRegressionRun>(`/agents/${agentId}/evolution/regression-runs`, { method: "POST" }),
  runContextCheck: (agentId: string) =>
    request<AgentEvolutionOverview["latestContextCheckRun"]>(
      `/agents/${agentId}/evolution/context-check-runs`,
      { method: "POST" }
    ),
  regressionStatus: (agentId: string) =>
    request<RegressionStatus>(`/agents/${agentId}/evolution/regression-status`),
  botBinding: (id: string) => request<AgentBotBindingAccess>(`/agents/${id}/bot-binding`),
  saveBotBinding: (id: string, body: {
    appId: string;
    appSecret?: string;
    verificationToken?: string;
    encryptKey?: string;
  }) => request<AgentBotBindingAccess>(`/agents/${id}/bot-binding`, {
    method: "PUT",
    body: JSON.stringify(body)
  }),
  activateBotBinding: (id: string) =>
    request<AgentBotBindingAccess>(`/agents/${id}/bot-binding/activate`, { method: "POST" }),
  disableBotBinding: (id: string) =>
    request<AgentBotBindingAccess>(`/agents/${id}/bot-binding/disable`, { method: "POST" }),
  removeBotBinding: (id: string) =>
    request<{ ok: boolean }>(`/agents/${id}/bot-binding`, { method: "DELETE" }),
  refreshFeishuIdentity,
  createAgent: (body: Record<string, unknown>) =>
    request<AgentSummary>("/agents", { method: "POST", body: JSON.stringify(body) }),
  updateDraft: (id: string, config: AgentConfig, expectedVersion: number) =>
    request<AgentSummary>(`/agents/${id}/draft`, {
      method: "PATCH",
      body: JSON.stringify({ config, expectedVersion })
    }),
  publish: (
    id: string,
    visibility: "private" | "company",
    draftVersion: number,
    acknowledgeRegression = false
  ) =>
    request<{ version: number }>(`/agents/${id}/publish`, {
      method: "POST",
      body: JSON.stringify({ visibility, draftVersion, acknowledgeRegression })
    }),
  debug: (id: string, message: string, conversationId?: string) =>
    request<{
      conversationId: string;
      content: string;
      modelId?: string;
      citations: Array<{ title: string; url?: string }>;
      tools: string[];
      durationMs: number;
    }>(`/agents/${id}/debug`, {
      method: "POST",
      body: JSON.stringify({ message, conversationId })
    }),
  assistPrompt: (id: string, mode: "generate" | "optimize", config: AgentConfig) =>
    request<{ prompt: string }>(`/agents/${id}/prompt-assist`, {
      method: "POST",
      body: JSON.stringify({ mode, config })
    }),
  activate: (id: string) =>
    request<{ activeAgentId: string; welcomeMessage: string }>(`/agents/${id}/activate`, {
      method: "POST"
    }),
  authorizeKnowledgeAccess: async () => {
    return authorizeFeishuAccess({ knowledge: true });
  },
  authorizeFeishuAccess,
  reauthorizeFeishuOfficeAccess,
  copy: (id: string) => request<AgentSummary>(`/agents/${id}/copy`, { method: "POST" }),
  remove: (id: string) => request(`/agents/${id}`, { method: "DELETE" }),
  versions: (id: string) =>
    request<Array<{ version: number; visibility: string; publishedAt: string }>>(
      `/agents/${id}/versions`
    ),
  rollback: (id: string, version: number) =>
    request(`/agents/${id}/rollback`, {
      method: "POST",
      body: JSON.stringify({ version })
    }),
  memories: (agentId: string, status: "active" | "candidate" | "all" = "active", q = "") =>
    request<AgentMemory[]>(
      `/agents/${agentId}/memories?status=${status}&q=${encodeURIComponent(q)}`
    ),
  createMemory: (
    agentId: string,
    body: { content: string; category: AgentMemory["category"]; isPinned?: boolean }
  ) =>
    request<AgentMemory>(`/agents/${agentId}/memories`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
  updateMemory: (
    id: string,
    body: Partial<Pick<AgentMemory, "content" | "category" | "isPinned" | "status">>
  ) =>
    request<AgentMemory>(`/memories/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
  removeMemory: (id: string) => request(`/memories/${id}`, { method: "DELETE" }),
  clearMemories: (agentId: string) =>
    request<{ ok: boolean; deletedCount: number }>(`/agents/${agentId}/memories`, {
      method: "DELETE"
    }),
  memorySettings: (agentId: string) =>
    request<AgentMemorySettings>(`/agents/${agentId}/memory-settings`),
  updateMemorySettings: (
    agentId: string,
    body: Partial<Pick<AgentMemorySettings, "enabled" | "autoLearn" | "temporaryMode">>
  ) =>
    request<AgentMemorySettings>(`/agents/${agentId}/memory-settings`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
  globalMemories: (status: "active" | "candidate" | "all" = "active", q = "") =>
    request<AgentMemory[]>(`/memories/global?status=${status}&q=${encodeURIComponent(q)}`),
  createGlobalMemory: (body: { content: string; category: AgentMemory["category"]; isPinned?: boolean }) =>
    request<AgentMemory>("/memories/global", { method: "POST", body: JSON.stringify(body) }),
  clearGlobalMemories: () =>
    request<{ ok: boolean; deletedCount: number }>("/memories/global", { method: "DELETE" }),
  globalMemorySettings: () => request<GlobalMemorySettings>("/memories/global/settings"),
  employeeProfile: () => request<EmployeeProfile>("/memories/global/profile"),
  saveEmployeeProfile: (content: string) =>
    request<EmployeeProfile>("/memories/global/profile", {
      method: "PUT",
      body: JSON.stringify({ content })
    }),
  updateGlobalMemorySettings: (body: Partial<Pick<GlobalMemorySettings, "enabled" | "autoLearn">>) =>
    request<GlobalMemorySettings>("/memories/global/settings", { method: "PATCH", body: JSON.stringify(body) }),
  projects: () => request<MemoryProject[]>("/projects"),
  projectEmployees: (q = "") =>
    request<Array<{ id: string; name: string; department: string }>>(`/projects/employees/search?q=${encodeURIComponent(q)}`),
  project: (id: string) => request<MemoryProjectDetail>(`/projects/${id}`),
  createProject: (body: { name: string; description?: string; agentIds?: string[] }) =>
    request<MemoryProject>("/projects", { method: "POST", body: JSON.stringify(body) }),
  projectMemories: (projectId: string, status: "active" | "candidate" | "all" = "active", q = "") =>
    request<AgentMemory[]>(`/projects/${projectId}/memories?status=${status}&q=${encodeURIComponent(q)}`),
  createProjectMemory: (projectId: string, body: { content: string; category: AgentMemory["category"]; isPinned?: boolean }) =>
    request<AgentMemory>(`/projects/${projectId}/memories`, { method: "POST", body: JSON.stringify(body) }),
  clearProjectMemories: (projectId: string) =>
    request<{ ok: boolean; deletedCount: number }>(`/projects/${projectId}/memories`, { method: "DELETE" }),
  addProjectAgent: (projectId: string, agentId: string) =>
    request(`/projects/${projectId}/agents/${agentId}`, { method: "POST" }),
  addProjectMember: (projectId: string, userId: string, role: "member" | "viewer" = "member") =>
    request(`/projects/${projectId}/members`, { method: "POST", body: JSON.stringify({ userId, role }) }),
  removeProjectMember: (projectId: string, userId: string) =>
    request(`/projects/${projectId}/members/${userId}`, { method: "DELETE" }),
  activateProject: (projectId: string, agentId: string) =>
    request<{ ok: boolean; projectName: string }>(`/projects/${projectId}/activate`, {
      method: "POST", body: JSON.stringify({ agentId })
    }),
  skillsMine: (q = "") =>
    request<CustomSkill[]>(`/skills/mine?q=${encodeURIComponent(q)}`),
  skillsMarket: (q = "") =>
    request<CustomSkill[]>(`/skills/market?q=${encodeURIComponent(q)}`),
  skillsAvailable: () => request<CustomSkill[]>("/skills/available"),
  getSkill: (id: string) => request<CustomSkill>(`/skills/${id}`),
  skillAgents: (id: string) =>
    request<import("./types").CapabilityAgentUsageResponse>(`/skills/${id}/agents`),
  createSkill: (body: {
    name: string;
    description: string;
    icon: string;
    tags: string[];
    instructions: string;
  }) => request<CustomSkill>("/skills", { method: "POST", body: JSON.stringify(body) }),
  installSkillPackage: (file: File) => {
    const body = new FormData();
    body.append("file", file);
    return request<CustomSkill>("/skills/install", { method: "POST", body });
  },
  updateSkill: (id: string, body: {
    name: string;
    description: string;
    icon: string;
    tags: string[];
    instructions: string;
  }) => request<CustomSkill>(`/skills/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  publishSkill: (id: string, visibility: "private" | "company") =>
    request<{ id: string; version: number }>(`/skills/${id}/publish`, {
      method: "POST",
      body: JSON.stringify({ visibility })
    }),
  removeSkill: (id: string) => request(`/skills/${id}`, { method: "DELETE" }),
  connectorsMine: (q = "") => request<Connector[]>(`/connectors/mine?q=${encodeURIComponent(q)}`),
  connectorsMarket: (q = "") => request<Connector[]>(`/connectors/market?q=${encodeURIComponent(q)}`),
  connectorsAvailable: () => request<Connector[]>("/connectors/available"),
  connectorReviews: () => request<Connector[]>("/connectors/reviews"),
  getConnector: (id: string) => request<Connector>(`/connectors/${id}`),
  connectorAgents: (id: string) =>
    request<import("./types").CapabilityAgentUsageResponse>(`/connectors/${id}/agents`),
  createConnector: (body: Omit<ConnectorSnapshot, "connectorId" | "sourceType" | "version">) =>
    request<Connector>("/connectors", { method: "POST", body: JSON.stringify(body) }),
  installConnector: (body: Omit<ConnectorSnapshot, "connectorId" | "sourceType" | "version">) =>
    request<Connector>("/connectors/install", { method: "POST", body: JSON.stringify(body) }),
  updateConnector: (id: string, body: Omit<ConnectorSnapshot, "connectorId" | "sourceType" | "version">) =>
    request<Connector>(`/connectors/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  submitConnectorReview: (id: string) =>
    request(`/connectors/${id}/submit-review`, { method: "POST" }),
  reviewConnector: (id: string, decision: "approved" | "rejected", notes: string) =>
    request(`/connectors/${id}/review`, { method: "POST", body: JSON.stringify({ decision, notes }) }),
  connectConnector: (id: string, credential?: string, metadata?: Record<string, unknown>) =>
    request<{ status: string }>(`/connectors/${id}/connect`, {
      method: "POST", body: JSON.stringify({ credential, metadata })
    }),
  startConnectorOAuth: (id: string) =>
    request<import("./types").CliOAuthSession>(`/connectors/${id}/oauth/start`, { method: "POST" }),
  completeConnectorOAuth: (id: string, authSessionId: string) =>
    request<{ status: "connected"; account?: unknown }>(`/connectors/${id}/oauth/complete`, {
      method: "POST",
      body: JSON.stringify({ authSessionId })
    }),
  disconnectConnector: (id: string) => request(`/connectors/${id}/connection`, { method: "DELETE" }),
  removeConnector: (id: string) => request(`/connectors/${id}`, { method: "DELETE" }),
  browserBridgeDevices: () => request<BrowserBridgeDevice[]>("/browser-bridge/devices"),
  createBrowserBridgePairing: (allowedOrigins: string[]) =>
    request<BrowserBridgePairing>("/browser-bridge/pairing-codes", {
      method: "POST",
      body: JSON.stringify({ allowedOrigins })
    }),
  updateBrowserBridgeDevice: (id: string, body: { name?: string; allowedOrigins?: string[] }) =>
    request<BrowserBridgeDevice>(`/browser-bridge/devices/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
  revokeBrowserBridgeDevice: (id: string) =>
    request(`/browser-bridge/devices/${id}`, { method: "DELETE" }),
  codexUsageLeaderboard: (period: "week" | "total") =>
    request<CodexUsageLeaderboard>(`/codex-usage/leaderboard?period=${period}`),
  codexUsageDevices: () => request<CodexUsageDevice[]>("/codex-usage/devices"),
  createCodexUsagePairing: () =>
    request<CodexUsagePairing>("/codex-usage/pairing-codes", { method: "POST" }),
  revokeCodexUsageDevice: (id: string) =>
    request<{ ok: boolean }>(`/codex-usage/devices/${id}`, { method: "DELETE" }),
  knowledge: () => request<KnowledgeSource[]>("/knowledge-sources"),
  addKnowledge: (body: Record<string, unknown>) =>
    request<KnowledgeSource>("/knowledge-sources", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  uploadObsidian: (
    files: File[],
    options: { title?: string; visibility: "private" | "company"; sourceId?: string }
  ) => {
    const body = new FormData();
    for (const file of files) body.append("files", file);
    if (options.title) body.append("title", options.title);
    body.append("visibility", options.visibility);
    return request<KnowledgeSource>(
      options.sourceId
        ? `/knowledge-sources/${options.sourceId}/obsidian`
        : "/knowledge-sources/obsidian",
      { method: "POST", body }
    );
  },
  uploadKnowledgeFiles: (
    files: File[],
    options: { title?: string; visibility: "private" | "company"; sourceId?: string }
  ) => {
    const body = new FormData();
    for (const file of files) body.append("files", file);
    if (options.title) body.append("title", options.title);
    body.append("visibility", options.visibility);
    return request<KnowledgeSource>(
      options.sourceId
        ? `/knowledge-sources/${options.sourceId}/upload`
        : "/knowledge-sources/upload",
      { method: "POST", body }
    );
  },
  updateKnowledgeVisibility: (id: string, visibility: "private" | "company") =>
    request<{ id: string; visibility: "private" | "company" }>(
      `/knowledge-sources/${id}/visibility`,
      { method: "PATCH", body: JSON.stringify({ visibility }) }
    ),
  syncKnowledge: (id: string) =>
    request(`/knowledge-sources/${id}/sync`, { method: "POST" }),
  removeKnowledge: (id: string) =>
    request(`/knowledge-sources/${id}`, { method: "DELETE" }),
  searchFeishu: (q: string) =>
    request<Array<Record<string, unknown>>>(`/feishu/documents/search?q=${encodeURIComponent(q)}`),
  resolveFeishuKnowledge: (url: string) =>
    request<{
      sourceType: "feishu_document" | "feishu_folder" | "feishu_wiki_space";
      externalId: string;
      resourceType: "wiki" | "docx" | "sheet" | "file" | "folder";
      title: string;
      url: string;
    }>("/feishu/documents/resolve", {
      method: "POST",
      body: JSON.stringify({ url })
    }),
  reauthorizeFeishu: async () => {
    const code = await requestFeishuAuthCode({ knowledge: true });
    const result = await request<{ user: { id: string; name: string; department: string } }>(
      "/auth/feishu/exchange",
      { method: "POST", body: JSON.stringify({ code }) }
    );
    authPromise = Promise.resolve(result);
    window.location.reload();
  }
};
